// frontend/src/components/Login.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../config/api';

const Login = ({ onLoginSuccess }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for token or error in URL (from OAuth callback)
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');

    if (token) {
      // Store token and redirect to dashboard
      localStorage.setItem('authToken', token);

      // Verify token is valid
      fetch(`${config.baseUrl}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data.valid) {
            onLoginSuccess(data.user);
            navigate('/');
          } else {
            setError('Invalid authentication token');
            localStorage.removeItem('authToken');
          }
        })
        .catch(err => {
          setError('Failed to verify authentication');
          localStorage.removeItem('authToken');
        });
    } else if (error) {
      setError(decodeURIComponent(error));
    }
  }, [location, navigate, onLoginSuccess]);

  const handleLogin = () => {
    setIsLoading(true);
    setError(null);

    // Redirect to backend OAuth endpoint
    window.location.href = `${config.baseUrl}/auth/login`;
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        <div style={styles.header}>
          <h1 style={styles.title}>Warehouse Management System</h1>
          <p style={styles.subtitle}>Sign in to continue</p>
        </div>

        {error && (
          <div style={styles.errorBox}>
            <span style={styles.errorIcon}>⚠</span>
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={handleLogin}
          disabled={isLoading}
          style={{
            ...styles.loginButton,
            ...(isLoading ? styles.loginButtonDisabled : {})
          }}
        >
          {isLoading ? (
            <span>Signing in...</span>
          ) : (
            <>
              <svg style={styles.microsoftIcon} viewBox="0 0 23 23">
                <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
                <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
                <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
              </svg>
              <span>Sign in with Microsoft</span>
            </>
          )}
        </button>

        <div style={styles.infoBox}>
          <p style={styles.infoText}>
            Use your Office 365 account to sign in
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  loginBox: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxWidth: '400px',
    width: '100%'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 10px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: 0
  },
  errorBox: {
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '4px',
    padding: '12px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    color: '#c33',
    fontSize: '14px'
  },
  errorIcon: {
    marginRight: '8px',
    fontSize: '18px'
  },
  loginButton: {
    width: '100%',
    padding: '12px 24px',
    backgroundColor: '#2f2f2f',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#1a1a1a'
    }
  },
  loginButtonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  microsoftIcon: {
    width: '21px',
    height: '21px',
    marginRight: '12px'
  },
  infoBox: {
    marginTop: '20px',
    padding: '12px',
    backgroundColor: '#f9f9f9',
    borderRadius: '4px',
    textAlign: 'center'
  },
  infoText: {
    fontSize: '13px',
    color: '#666',
    margin: 0
  }
};

export default Login;
